import {
  blogpostReferenceValidator,
  pageReferenceValidator,
  spaceReferenceValidator,
  userReferenceValidator,
} from '@databases/types/confluence';
import {
  databaseCreateOptionsValidator,
  databaseFieldTypeValidator,
  entryCreateOptionsValidator,
} from '@databases/types/database';
import {
  ALL_ENTRIES_DATABASE_VIEW,
  databaseViewSettingsValidator,
  databaseViewValidator,
} from '@databases/types/database/view';
import {
  fieldSettingsValidator,
  spaceKeyOptionalValidator,
} from '@databases/types/macros';
import {
  booleanStringValidator,
  jsonStringValidator,
  transformValidator,
} from '@databases/types/validators';
import { Cheerio, Element } from 'cheerio';
import { z } from 'zod';

import { serializePageReference, serializeSpaceReference } from './confluence/utils';
import {
  DATABASE_ENTRY_MACRO_KEY,
  DATABASE_MACRO_KEY,
  DATABASE_VALUE_MACRO_KEY,
  DB_ENTRY_MACRO_KEY,
  DB_FIELD_MACRO_KEY,
  DB_MACRO_KEY,
  DB_VALUE_MACRO_KEY,
} from './constants';
import { cheerioIterator } from './lib/dom';
import { encodeCdata, encodeHtml } from './utils';
import { numberStringValidator } from './validators';

const spaceKeyValidator = transformValidator(
  z.string(),
  (val) => ({ key: val }),
  spaceReferenceValidator
);

const defaultViewValidator = transformValidator(
  z.string().optional(),
  (val) => {
    if (val && val.startsWith('{')) {
      return JSON.parse(val);
    } else {
      return val;
    }
  },
  z.string().or(databaseViewSettingsValidator).optional()
);

/**
 * A zod schema for the parameters of each Databases macro.
 */
const macroParameterValidators = {
  [DATABASE_MACRO_KEY]: z.object({
    databaseSpace: spaceKeyValidator,
    databaseId: z.string().optional(), // Can be undefined if create is defined
    toolbar: booleanStringValidator(z.boolean().optional()),
    showName: booleanStringValidator(z.boolean().optional()),
    /** A string represents a saved view ID, an object represents custom view settings. */
    defaultView: defaultViewValidator,
    create: jsonStringValidator(databaseCreateOptionsValidator.optional()),
    lockFields: booleanStringValidator(z.boolean().optional()),
    lockContent: booleanStringValidator(z.boolean().optional()),
  }),
  [DATABASE_ENTRY_MACRO_KEY]: z.object({
    databaseSpace: spaceKeyValidator,
    databaseId: z.string(),
    entryId: z.string().optional(), // Can be undefined if create is defined
    create: jsonStringValidator(entryCreateOptionsValidator.optional()),
    lockFields: booleanStringValidator(z.boolean().optional()),
    lockContent: booleanStringValidator(z.boolean().optional()),
  }),
  [DATABASE_VALUE_MACRO_KEY]: z.object({
    databaseSpace: spaceKeyValidator,
    databaseId: z.string(),
    fieldId: z.string(),
    entryId: z.string(),
  }),
  [DB_MACRO_KEY]: z.object({
    id: z.string(),
    mainFieldId: z.string().optional(),
    coverFieldId: z.string().optional(),
    views: jsonStringValidator(
      z.array(databaseViewValidator).default([ALL_ENTRIES_DATABASE_VIEW])
    ),
    defaultViewId: z.string().default(ALL_ENTRIES_DATABASE_VIEW.id),
  }),
  [DB_FIELD_MACRO_KEY]: fieldSettingsValidator.extend({
    id: z.string(),
    type: databaseFieldTypeValidator,
    width: numberStringValidator(z.number().optional()),
    description: z.string().optional(),
  }),
  [DB_ENTRY_MACRO_KEY]: z.object({
    id: z.string(),
  }),
  [DB_VALUE_MACRO_KEY]: z.object({
    fieldId: z.string(),
    entryId: z.string(),
    type: databaseFieldTypeValidator,
    databaseSpace: spaceKeyOptionalValidator,
    databaseId: z.string().optional(),
    databaseEntryId: z.string().optional(),
    users: jsonStringValidator(z.array(userReferenceValidator).optional()),
    selection: jsonStringValidator(z.array(z.string()).optional()),
  }),
  jira: z.object({
    /** Comma-separated list of columns to show. {@see JiraColumn} */
    columns: z.string().optional(),
    /** JQL query (for multi issue macro). Either this or key must be specified. */
    jqlQuery: z.string().optional(),
    /** Issue key (for single issue macro). Either this or jqlQuery must be specified. */
    key: z.string().optional(),
    /** Jira instance display name */
    server: z.string().optional(),
    /** Jira instance server ID */
    serverId: z.string().optional(),
    /** Jira instance cloud ID */
    cloudId: z.string().optional(),
    /** Maximum number of issues to show */
    maximumIssues: z.number().optional(),
  }),
  'excerpt-include': z.object({
    '': pageReferenceValidator.or(blogpostReferenceValidator),
  }),
  test: z.object({
    // Used in unit tests
    param1: z.string().optional(),
  }),
};

export enum MacroBodyType {
  NONE = 'none',
  PLAIN_TEXT = 'plain-text',
  RICH_TEXT = 'rich-text',
}

/**
 * The body type of each macro. Must match the definition in atlassian-connect.json. The default is NONE.
 */
let macroBodyTypes = {
  [DATABASE_MACRO_KEY]: MacroBodyType.NONE,
  [DATABASE_ENTRY_MACRO_KEY]: MacroBodyType.NONE,
  [DATABASE_VALUE_MACRO_KEY]: MacroBodyType.NONE,
  [DB_MACRO_KEY]: MacroBodyType.NONE,
  [DB_FIELD_MACRO_KEY]: MacroBodyType.RICH_TEXT,
  [DB_ENTRY_MACRO_KEY]: MacroBodyType.NONE,
  [DB_VALUE_MACRO_KEY]: MacroBodyType.RICH_TEXT,
  jira: MacroBodyType.NONE,
  'excerpt-include': MacroBodyType.NONE,
  test: MacroBodyType.NONE, // Used in unit tests
};

// For unit tests
export function _setMacroBodyTypes(types: any): void {
  macroBodyTypes = types;
}

/**
 * The key of a Databases macro.
 */
export type MacroKey = keyof typeof macroParameterValidators;

/**
 * The parsed parameters of a Databases macro.
 */
export type MacroParameters<Key extends MacroKey> = z.infer<
  (typeof macroParameterValidators)[Key]
>;

/**
 * The encoded macro parameters (as used by AP.confluence.getMacroData() and AP.confluence.saveMacro()). Each
 * value is encoded as a string.
 */
export type EncodedMacroParameters<Key extends MacroKey> = Record<
  keyof MacroParameters<Key>,
  string
>;

/**
 * Encode the given macro parameters to the format understood by the AP helper methods, where each value is encoded
 * as a string.
 */
export function encodeMacroParameters<Key extends MacroKey>(
  parameters: MacroParameters<Key>
): EncodedMacroParameters<Key> {
  return Object.fromEntries(
    Object.entries(parameters).flatMap(([key, value]) => {
      if (value == null) {
        return [];
      } else if (typeof value === 'string') {
        return [[key, value]];
      } else if (value.spaceKey) {
        return [[key, `${value.spaceKey}:${value.contentTitle}`]];
      } else if (value.key) {
        return [[key, value.key]];
      } else {
        return [[key, JSON.stringify(value)]];
      }
    })
  ) as any;
}

/**
 * Parse the given encoded macro parameters. Throw an error if they don't fit the schema.
 * @throws Throws an error if the macro parameters do not match the schema or required parameters are missing.
 */
export function parseMacroParameters<Key extends MacroKey>(
  macroKey: Key,
  params: Record<string, string>
): MacroParameters<Key> {
  return macroParameterValidators[macroKey].parse(params);
}

/**
 * Parse the given encoded macro parameters. All parameters are considered optional and set to undefined if they are
 * not set. This can be used to support newly created macros where required parameters are not set yet, but existing
 * parameters should still have the right data type.
 * @throws Throws an error if the macro parameters do not match the schema.
 */
export function parsePartialMacroParameters<Key extends MacroKey>(
  macroKey: Key,
  params: Record<string, string>
): Partial<MacroParameters<Key>> {
  return macroParameterValidators[macroKey].partial().parse(params) as any;
}

/**
 * Serialize macro parameters to XHTML storage format.
 */
export function serializeMacroParameters(
  parameters: Partial<MacroParameters<MacroKey>>
): string {
  return Object.entries(parameters)
    .flatMap(([key, value]) => {
      if (value == null) {
        return [];
      } else if (typeof value === 'string') {
        return [[key, encodeHtml(value)]];
      } else if (value.spaceKey) {
        return [[key, serializePageReference(value, true)]];
      } else if (value.key) {
        return [[key, serializeSpaceReference(value)]];
      } else {
        return [[key, encodeHtml(JSON.stringify(value))]];
      }
    })
    .map(
      ([key, html]) => `<ac:parameter ac:name="${encodeHtml(key)}">${html}</ac:parameter>`
    )
    .join('');
}

export enum MacroLayout {
  DEFAULT = 'default',
  WIDE = 'wide',
  FULL_WIDTH = 'full-width',
}

export interface MacroOptions {
  layout?: MacroLayout;
  macroId?: string;
}

export type MacroBody = { html: string } | string | undefined;

/**
 * Serialize a macro to XHTML storage format.
 */
export function serializeMacro<Key extends MacroKey>(
  macroKey: Key,
  parameters: MacroParameters<MacroKey>,
  body: MacroBody = undefined,
  options: MacroOptions = {}
): string {
  const idHtml = options.macroId ? ` ac:macro-id="${encodeHtml(options.macroId)}"` : '';
  const layoutHtml = options.layout ? ` data-layout="${encodeHtml(options.layout)}"` : '';
  const paramsHtml = serializeMacroParameters(parameters);
  const bodyElHtml = serializeMacroBody(macroKey, body);
  return `<ac:structured-macro ac:name="${encodeHtml(
    macroKey
  )}"${idHtml}${layoutHtml}>${paramsHtml}${bodyElHtml}</ac:structured-macro>`;
}

export function serializeMacroBody(
  macroKey: MacroKey,
  body: MacroBody = undefined
): string {
  if (macroBodyTypes[macroKey] === MacroBodyType.RICH_TEXT) {
    return typeof body === 'object'
      ? `<ac:rich-text-body>${body.html}</ac:rich-text-body>`
      : body
      ? `<ac:rich-text-body>${encodeHtml(body)}</ac:rich-text-body>`
      : '';
  } else if (macroBodyTypes[macroKey] === MacroBodyType.PLAIN_TEXT) {
    if (typeof body === 'object') {
      throw new Error('Cannot serialize rich text as a plain text body.');
    }
    return body ? `<ac:plain-text-body>${encodeCdata(body)}</ac:plain-text-body>` : '';
  } else if (body) {
    throw new Error('Cannot serialize a body for a bodyless macro.');
  } else {
    return '';
  }
}

/**
 * Parse macro parameters from XHTML storage format.
 * @param el The DOM element of the macro definition.
 * @param spaceKey The space key where the content containing the macro lives. This is used to resolve same-space page references.
 */
export function deserializeMacroParameters<Key extends MacroKey>(
  macroKey: Key,
  el: Cheerio<Element>,
  spaceKey: string
): MacroParameters<Key> {
  const encoded: Record<string, string> = {};

  for (const param of cheerioIterator(el.children('ac\\:parameter'))) {
    const name = param.attr('ac:name');
    if (!name) {
      continue;
    }

    const riPage = param.children('ac\\:link').children('ri\\:page');
    if (riPage.length > 0) {
      encoded[name] = `${riPage.attr('ri:space-key') ?? spaceKey}:${
        riPage.attr('ri:content-title') ?? ''
      }`;
      continue;
    }

    const riSpace = param.children('ri\\:space');
    if (riSpace.length > 0) {
      encoded[name] = riSpace.attr('ri:space-key') ?? '';
      continue;
    }

    encoded[name] = param.text();
  }

  return parseMacroParameters(macroKey, encoded);
}

export function getMacroBody(el: Cheerio<Element>): Cheerio<Element> {
  return el.children('ac\\:plain-text-body,ac\\:rich-text-body');
}

/**
 * In the given parsed storage format body, find a macro instance with the given macro ID.
 */
export function findMacroById(el: Cheerio<Element>, macroId: string): Cheerio<Element> {
  return el.find(`ac\\:structured-macro[ac\\:macro-id=${macroId}]`);
}

/**
 * In the given parsed storage format body, find all macro instance with the given macro key.
 */
export function findMacrosByKey(el: Cheerio<Element>, key: MacroKey): Cheerio<Element> {
  return el.find(`ac\\:structured-macro[ac\\:name=${key}]`);
}
